// src/services/dataLoader.ts

import type { GraphData, WordFrequencies } from "@synapse/engine";
import { API_BASE_URL } from "../config/api";
import { getGraphCache } from "./storage/graphCache";

export type { GraphData, WordFrequencies } from "@synapse/engine";

export interface DefinitionsData {
  [word: string]: string[];
}

/** The loaded graph's identity, read from its payload (never computed client-side). */
export interface GraphIdentity {
  vocabHash: string;
  graphHash: string;
}

/** The persisted D1 graph, tagged with its identity (vocab-v2 §5.1). */
interface TaggedGraphCache {
  /** Server graph id; absent in caches written before ids were tracked. */
  graph_id?: string;
  vocab_hash: string;
  graph_hash: string;
  words: GraphData;
}

const HASH_RE = /^[0-9a-f]{64}$/;

let graphDataCache: GraphData | null = null;
let bundledGraph: GraphData | null = null;
let bundledIdentity: GraphIdentity | null = null;
let loadedIdentity: GraphIdentity | null = null;
let loadedGraphId: string | null = null;
let wordFrequenciesCache: WordFrequencies | null = null;
let definitionsCache: DefinitionsData | null = null;
let loadedGraphVersion: string | null = null;

/** Freshness key: graph_hash covers the edges, the id the rest of the payload. */
const freshnessKey = (id: string | null | undefined, graphHash: string) =>
  `${id ?? ""}:${graphHash}`;

const toIdentity = (vocab: unknown, graph: unknown): GraphIdentity | null =>
  typeof vocab === "string" &&
  typeof graph === "string" &&
  HASH_RE.test(vocab) &&
  HASH_RE.test(graph)
    ? { vocabHash: vocab, graphHash: graph }
    : null;

/** A decoded cache record; null when untagged (legacy), corrupt, or empty. */
export const validateGraphCache = (value: unknown): TaggedGraphCache | null => {
  const obj = value as Partial<TaggedGraphCache> | null | undefined;
  if (!obj || typeof obj !== "object") return null;
  if (!toIdentity(obj.vocab_hash, obj.graph_hash)) return null;
  if (!obj.words || typeof obj.words !== "object") return null;
  if (Object.keys(obj.words).length === 0) return null;
  return obj as TaggedGraphCache;
};

/** The graph shipped with this release. Daily challenges are always selected from it. */
export const loadBundledGraph = async (): Promise<GraphData> => {
  if (bundledGraph) return bundledGraph;
  const graphDataModule = await import("../data/graph.json");
  const json = (graphDataModule.default || graphDataModule) as unknown as {
    nodes?: GraphData;
    version?: string;
    vocab_hash?: string;
    graph_hash?: string;
  };
  bundledGraph = json.nodes ?? (json as unknown as GraphData);
  bundledIdentity = toIdentity(json.vocab_hash, json.graph_hash);
  loadedGraphVersion = json.version ?? null;
  return bundledGraph;
};

/** A tagged cache with the bundle's vocabulary, else null (other caches are discarded). */
async function readGraphCache(): Promise<TaggedGraphCache | null> {
  let value: unknown;
  try {
    value = await getGraphCache().read();
  } catch {
    return null;
  }
  if (value === undefined || value === null) return null;
  const cache = validateGraphCache(value);
  if (
    cache &&
    bundledIdentity &&
    cache.vocab_hash === bundledIdentity.vocabHash
  ) {
    return cache;
  }
  // Untagged legacy, corrupt, or another release's vocabulary: never run it
  // (approved narrowing of spec §5.4 — dailies from the bundle must exist in the
  // play graph).
  try {
    await getGraphCache().remove();
  } catch {
    // ignore — the bundle is used either way
  }
  return null;
}

/**
 * Bundled-first: return the bundle (or a tagged D1 cache sharing the bundle's
 * vocabulary) with no network call on the hot path, then refresh from D1 in the
 * background for the NEXT launch. The session's identity is fixed here.
 */
let graphLoad: Promise<GraphData> | null = null;

export const loadGraphData = (): Promise<GraphData> => {
  // Concurrent callers share one load, so the session has one identity and
  // the background refresh runs once.
  graphLoad ??= (async () => {
    const bundle = await loadBundledGraph();
    const cache = await readGraphCache();
    graphDataCache = cache ? cache.words : bundle;
    loadedIdentity = cache
      ? toIdentity(cache.vocab_hash, cache.graph_hash)
      : bundledIdentity;
    // The bundle's version mirrors the D1 graph id it was built from.
    loadedGraphId = cache ? (cache.graph_id ?? null) : loadedGraphVersion;
    // Background refresh — never blocks, never swaps the graph or identity mid-session.
    void refreshGraphFromApi();
    return graphDataCache;
  })().catch((error: unknown) => {
    graphLoad = null;
    throw error;
  });
  return graphLoad;
};

/**
 * Keep the cache in step with the active D1 graph for the NEXT launch. Asks the
 * small meta endpoint first and downloads the full graph (~635KB gz) only when
 * its id:graph_hash key differs from the graph this launch runs. A graph on another
 * vocabulary is never fetched: readGraphCache would discard it. Best-effort;
 * offline, a missing endpoint, or a legacy row without hashes are ignored.
 */
async function refreshGraphFromApi(): Promise<void> {
  try {
    const metaRes = await fetch(`${API_BASE_URL}/api/v1/graphs/active/meta`);
    if (!metaRes.ok) return;
    const meta = (await metaRes.json()) as {
      id?: string;
      vocab_hash?: string;
      graph_hash?: string;
    };
    const remote = toIdentity(meta.vocab_hash, meta.graph_hash);
    if (!remote || !bundledIdentity) return;
    if (remote.vocabHash !== bundledIdentity.vocabHash) return;
    const remoteKey = freshnessKey(meta.id, remote.graphHash);
    if (
      loadedIdentity &&
      remoteKey === freshnessKey(loadedGraphId, loadedIdentity.graphHash)
    ) {
      return;
    }
    if (
      remoteKey === freshnessKey(loadedGraphVersion, bundledIdentity.graphHash)
    ) {
      // The server is back on the bundled graph: the cache is out of date.
      await getGraphCache().remove();
      return;
    }

    const res = await fetch(`${API_BASE_URL}/api/v1/graphs/active`);
    if (!res.ok) return;
    const data = (await res.json()) as {
      words?: GraphData;
      meta?: { id?: string; vocab_hash?: string; graph_hash?: string };
    };
    const cache = validateGraphCache({
      graph_id: data.meta?.id,
      vocab_hash: data.meta?.vocab_hash,
      graph_hash: data.meta?.graph_hash,
      words: data.words,
    });
    if (!cache || cache.vocab_hash !== bundledIdentity.vocabHash) return;
    await getGraphCache().write(cache);
  } catch {
    // offline / API down — keep the bundled graph
  }
}

/**
 * Fetch definitions from API graph response or fall back to bundled JSON.
 */
export const loadDefinitions = async (): Promise<DefinitionsData> => {
  if (definitionsCache) return definitionsCache;

  // If the active graph carries inline definitions (i.e. it was persisted from a
  // D1 background refresh — the Workers API serves definitions inline), use them so
  // definitions match that graph's words. The bundled offline default has none, so
  // this is skipped for it and we fall through to bundled definitions.json.
  if (graphDataCache) {
    const defs: DefinitionsData = {};
    for (const [word, data] of Object.entries(graphDataCache)) {
      const wordData = data as GraphData[string] & { definitions?: string[] };
      if (wordData.definitions) {
        defs[word] = wordData.definitions;
      }
    }
    if (Object.keys(defs).length > 0) {
      definitionsCache = defs;
      return definitionsCache;
    }
  }

  // Fall back to bundled definitions.json
  const definitionsModule = await import("../data/definitions.json");
  const definitions = definitionsModule.default || definitionsModule;

  if (typeof definitions === "object" && definitions !== null) {
    definitionsCache = definitions as DefinitionsData;
    return definitionsCache;
  }

  throw new Error("Failed to load definitions.");
};

export const loadWordFrequencies = async (): Promise<WordFrequencies> => {
  if (wordFrequenciesCache) return wordFrequenciesCache;

  const wordFrequenciesModule =
    await import("../data/filtered_word_frequencies.json");
  const wordFrequencies =
    wordFrequenciesModule.default || wordFrequenciesModule;

  if (typeof wordFrequencies === "object" && wordFrequencies !== null) {
    wordFrequenciesCache = wordFrequencies as WordFrequencies;
    return wordFrequenciesCache;
  }

  throw new Error("Failed to load word frequencies.");
};

export const clearDataCache = (): void => {
  graphLoad = null;
  graphDataCache = null;
  bundledGraph = null;
  bundledIdentity = null;
  loadedIdentity = null;
  loadedGraphId = null;
  wordFrequenciesCache = null;
  definitionsCache = null;
  loadedGraphVersion = null;
};

export const getLoadedGraphVersion = (): string | null => loadedGraphVersion;

export const getLoadedGraphIdentity = (): GraphIdentity | null =>
  loadedIdentity;

export const loadDefinitionsData = loadDefinitions;
